import { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { doOpenCall } from '../../_utilities/doOpenCall'
import { postBookingHeightToParent } from '../../_utilities/bookingPostMessage'
import type { CalendarEventType } from '../../_types/calendar-event-type'
import type { AvailabilitySlot } from '../../_types/availability'
import type { CreateBookingResult } from '../../_types/calendar-booking'
import SlotPicker from '../../components/slot-picker/SlotPicker'
import GuestForm from '../../components/guest-form/GuestForm'
import type { GuestFormValues } from '../../components/guest-form/types'
import BookingFormIframe from '../../components/booking-form-iframe/BookingFormIframe'
import BookingConfirmation from '../../components/booking-confirmation/BookingConfirmation'
import { detectBrowserTimezone } from '../../_data/timezones'
import {
  getLocationLabel,
  parsePrefillValues,
  parseViewMode,
  resolveFormsBaseUrl,
} from './helpers'
import {
  BackButton,
  ComingSoon,
  ErrorState,
  EventTypeDescription,
  EventTypeImage,
  EventTypeProperty,
  EventTypeTitle,
  LeftColumn,
  LoadingState,
  PageShell,
  RightColumn,
  TwoColumnLayout,
} from './styled'

type BookingFlowStep = 'picking-slot' | 'entering-details' | 'confirmed'

const BookingPage = (): JSX.Element => {
  const parameters = useParams<{ agentUserId: string; slug: string }>()
  const [searchParameters] = useSearchParams()
  const viewMode = useMemo(
    () => parseViewMode(searchParameters),
    [searchParameters],
  )
  const prefillValues = useMemo(
    () => parsePrefillValues(searchParameters),
    [searchParameters],
  )
  const quickPassUserId = useMemo(
    () => (searchParameters.get('qp') ?? '').trim(),
    [searchParameters],
  )
  const formsBaseUrl = useMemo(() => resolveFormsBaseUrl(), [])

  const [eventType, setEventType] = useState<CalendarEventType | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [errorMessage, setErrorMessage] = useState<string>('')

  const [flowStep, setFlowStep] = useState<BookingFlowStep>('picking-slot')
  const [selectedSlot, setSelectedSlot] = useState<AvailabilitySlot | null>(
    null,
  )
  const [selectedDurationMinutes, setSelectedDurationMinutes] =
    useState<number>(0)
  const [isSubmittingBooking, setIsSubmittingBooking] =
    useState<boolean>(false)
  const [submitError, setSubmitError] = useState<string>('')
  const [confirmedBooking, setConfirmedBooking] =
    useState<CreateBookingResult | null>(null)
  const [confirmedGuest, setConfirmedGuest] = useState<GuestFormValues | null>(
    null,
  )

  const browserTimezone = useMemo(() => detectBrowserTimezone(), [])
  const [viewerTimezone, setViewerTimezone] = useState<string>(browserTimezone)

  const loadEventType = useCallback(async (): Promise<void> => {
    if (!parameters.agentUserId || !parameters.slug) {
      setErrorMessage('This link looks incomplete.')
      setIsLoading(false)
      return
    }
    setIsLoading(true)
    try {
      const response = await doOpenCall('publiccalendar-geteventtypebyslug', {
        agentUserId: parameters.agentUserId,
        slug: parameters.slug,
      })
      if (response.code === 200) {
        setEventType(JSON.parse(response.data) as CalendarEventType)
        setErrorMessage('')
      } else {
        setErrorMessage(response.message || 'Event type not found.')
      }
    } catch {
      setErrorMessage('Could not reach the booking service. Try again shortly.')
    } finally {
      setIsLoading(false)
    }
  }, [parameters.agentUserId, parameters.slug])

  useEffect(() => {
    loadEventType()
  }, [loadEventType])

  useEffect(() => {
    if (viewMode !== 'minimal' || typeof ResizeObserver === 'undefined') {
      return undefined
    }
    const postHeight = (): void => {
      postBookingHeightToParent(document.documentElement.scrollHeight)
    }
    postHeight()
    const observer = new ResizeObserver(postHeight)
    observer.observe(document.body)
    return () => {
      observer.disconnect()
    }
  }, [viewMode])

  const handleSelectSlot = useCallback(
    (slot: AvailabilitySlot, durationMinutes: number): void => {
      setSelectedSlot(slot)
      setSelectedDurationMinutes(durationMinutes)
      setFlowStep('entering-details')
      setSubmitError('')
    },
    [],
  )

  const handleCancelDetails = useCallback((): void => {
    setFlowStep('picking-slot')
    setSubmitError('')
  }, [])

  const [turnstileResetSignal, setTurnstileResetSignal] = useState<number>(0)

  const submitBookingToServer = useCallback(
    async (payload: {
      guestName: string
      guestEmail: string
      guestPhone: string
      guestNotes: string
      additionalAttendees: string[]
      recurring: boolean
      customFormId: string | null
      customFormResponses: Record<string, unknown>
      turnstileToken: string
    }): Promise<void> => {
      if (!eventType || !selectedSlot) {
        return
      }
      setIsSubmittingBooking(true)
      setSubmitError('')
      try {
        const response = await doOpenCall('publiccalendar-createbooking', {
          eventTypeId: eventType.id,
          agentUserId: eventType.agentUserId,
          startTime: selectedSlot.startTime,
          durationMinutes: selectedDurationMinutes,
          guestName: payload.guestName,
          guestEmail: payload.guestEmail,
          guestPhone: payload.guestPhone,
          guestNotes: payload.guestNotes,
          quickPassUserId,
          additionalAttendees: payload.additionalAttendees,
          recurring: payload.recurring,
          guestTimezone: viewerTimezone,
          customFormId: payload.customFormId,
          customFormResponses: payload.customFormResponses,
          turnstileToken: payload.turnstileToken,
          formSubmissionMetadata: {
            userAgent: window.navigator.userAgent,
            referrer: document.referrer,
          },
        })
        if (response.code === 200) {
          const booking = JSON.parse(response.data) as CreateBookingResult
          setConfirmedBooking(booking)
          setConfirmedGuest({
            name: payload.guestName,
            email: payload.guestEmail,
            phone: payload.guestPhone,
            notes: payload.guestNotes,
            additionalAttendees: payload.additionalAttendees,
            recurring: payload.recurring,
            turnstileToken: '',
          })
          setFlowStep('confirmed')
        } else if (response.code === 409) {
          setSubmitError(
            'That time was just taken. Please pick another slot.',
          )
          setFlowStep('picking-slot')
          setSelectedSlot(null)
          setTurnstileResetSignal(previous => previous + 1)
        } else {
          setSubmitError(response.message || 'Failed to create booking.')
          setTurnstileResetSignal(previous => previous + 1)
        }
      } catch {
        setSubmitError(
          'Could not reach the booking service. Check your connection and try again.',
        )
        setTurnstileResetSignal(previous => previous + 1)
      } finally {
        setIsSubmittingBooking(false)
      }
    },
    [
      eventType,
      selectedSlot,
      selectedDurationMinutes,
      viewerTimezone,
      quickPassUserId,
    ],
  )

  const handleSubmitBooking = useCallback(
    async (values: GuestFormValues): Promise<void> => {
      await submitBookingToServer({
        guestName: values.name,
        guestEmail: values.email,
        guestPhone: values.phone,
        guestNotes: values.notes,
        additionalAttendees: values.additionalAttendees,
        recurring: values.recurring,
        customFormId: null,
        customFormResponses: {},
        turnstileToken: values.turnstileToken,
      })
    },
    [submitBookingToServer],
  )

  const handleFormIframeSubmitted = useCallback(
    async (submission: {
      formId: string
      responses: Record<string, unknown>
      extractedName: string
      extractedEmail: string
      extractedPhone: string
      turnstileToken: string
    }): Promise<void> => {
      if (submission.extractedEmail.length === 0) {
        setSubmitError(
          'The form is missing an email field. Please contact the host.',
        )
        return
      }
      if (
        typeof submission.turnstileToken !== 'string' ||
        submission.turnstileToken.length === 0
      ) {
        setSubmitError(
          'Please complete the bot verification challenge before booking.',
        )
        return
      }
      await submitBookingToServer({
        guestName: submission.extractedName,
        guestEmail: submission.extractedEmail,
        guestPhone: submission.extractedPhone,
        guestNotes: '',
        additionalAttendees: [],
        recurring: false,
        customFormId: submission.formId,
        customFormResponses: submission.responses,
        turnstileToken: submission.turnstileToken,
      })
    },
    [submitBookingToServer],
  )

  const handleFormIframeError = useCallback((message: string): void => {
    setSubmitError(message)
  }, [])

  const usesCustomForm =
    eventType !== null &&
    (eventType.customFormType === 'custom' ||
      eventType.customFormType === 'flow') &&
    eventType.customFormSlug.length > 0

  const bookingContextForIframe = useMemo(() => {
    if (!eventType || !selectedSlot) {
      return null
    }
    return {
      agentUserId: eventType.agentUserId,
      eventTypeId: eventType.id,
      eventTypeSlug: eventType.slug,
      slot: {
        startTime: selectedSlot.startTime,
        endTime: selectedSlot.endTime,
        durationMinutes: selectedDurationMinutes,
      },
      timezone: eventType.timezone || browserTimezone,
      prefillValues: {
        email: prefillValues.email,
        name: prefillValues.name,
      },
    }
  }, [
    eventType,
    selectedSlot,
    selectedDurationMinutes,
    browserTimezone,
    prefillValues,
  ])

  if (isLoading) {
    return (
      <PageShell view={viewMode}>
        <LoadingState>Loading…</LoadingState>
      </PageShell>
    )
  }

  if (errorMessage.length > 0 || !eventType) {
    return (
      <PageShell view={viewMode}>
        <ErrorState>{errorMessage || 'Event type not found.'}</ErrorState>
      </PageShell>
    )
  }

  return (
    <PageShell view={viewMode}>
      <TwoColumnLayout view={viewMode}>
        {viewMode === 'full' ? (
          <LeftColumn>
            {eventType.imageUrl && eventType.imageUrl.length > 0 ? (
              <EventTypeImage
                src={eventType.imageUrl}
                alt={eventType.title}
                loading="eager"
              />
            ) : null}
            <EventTypeTitle>{eventType.title}</EventTypeTitle>
            {eventType.propertyName && eventType.propertyName.length > 0 ? (
              <EventTypeProperty>{eventType.propertyName}</EventTypeProperty>
            ) : null}
            <EventTypeDescription
              dangerouslySetInnerHTML={{ __html: eventType.description || '' }}
            />
          </LeftColumn>
        ) : null}
        <RightColumn view={viewMode}>
          {flowStep === 'picking-slot' ? (
            <SlotPicker
              agentUserId={eventType.agentUserId}
              eventTypeId={eventType.id}
              durationOptionsMinutes={
                eventType.durationOptionsMinutes &&
                eventType.durationOptionsMinutes.length > 0
                  ? eventType.durationOptionsMinutes
                  : [eventType.durationMinutes]
              }
              defaultDurationMinutes={eventType.durationMinutes}
              slotCapacity={
                typeof eventType.slotCapacity === 'number' &&
                eventType.slotCapacity > 1
                  ? eventType.slotCapacity
                  : 1
              }
              hostTimezone={eventType.timezone || browserTimezone}
              viewerTimezone={viewerTimezone}
              onChangeViewerTimezone={setViewerTimezone}
              selectedSlot={selectedSlot}
              onSelectSlot={handleSelectSlot}
            />
          ) : null}
          {flowStep === 'entering-details' &&
          selectedSlot &&
          eventType &&
          usesCustomForm &&
          bookingContextForIframe ? (
            <>
              <BackButton
                type="button"
                onClick={handleCancelDetails}
                disabled={isSubmittingBooking}
              >
                ← Back to calendar
              </BackButton>
              <BookingFormIframe
                formsBaseUrl={formsBaseUrl}
                companyId={eventType.companyId}
                formType={
                  eventType.customFormType === 'flow' ? 'flow' : 'custom'
                }
                formSlug={eventType.customFormSlug}
                context={bookingContextForIframe}
                onSubmitted={handleFormIframeSubmitted}
                onError={handleFormIframeError}
              />
            </>
          ) : null}
          {flowStep === 'entering-details' &&
          selectedSlot &&
          !usesCustomForm ? (
            <GuestForm
              initialValues={{
                name: prefillValues.name,
                email: prefillValues.email,
              }}
              submitLabel="Confirm booking"
              isSubmitting={isSubmittingBooking}
              errorMessage={submitError}
              allowAdditionalAttendees={
                eventType?.allowAdditionalAttendees === true
              }
              recurringHint={
                eventType?.recurringEnabled === true &&
                eventType.recurrenceCount > 1
                  ? {
                      frequency: eventType.recurrenceFrequency,
                      count: eventType.recurrenceCount,
                    }
                  : null
              }
              turnstileResetSignal={turnstileResetSignal}
              onCancel={handleCancelDetails}
              onSubmit={handleSubmitBooking}
            />
          ) : null}
          {flowStep === 'confirmed' &&
          confirmedBooking !== null &&
          confirmedGuest !== null ? (
            <BookingConfirmation
              headline="You’re booked."
              subline="A confirmation email is on its way."
              bookingId={confirmedBooking.id}
              guestName={confirmedGuest.name}
              guestEmail={confirmedGuest.email}
              startTime={confirmedBooking.startTime}
              endTime={confirmedBooking.endTime}
              timezone={viewerTimezone}
              hostTimezone={eventType.timezone || browserTimezone}
              eventTypeTitle={eventType.title}
              locationLabel={getLocationLabel(eventType)}
              descriptionHtml={eventType.description}
              videoRoomUrl={confirmedBooking.videoRoomUrl}
              assignedAgentName={confirmedBooking.assignedAgentName ?? ''}
              rescheduleToken={confirmedBooking.rescheduleToken}
              cancelToken={confirmedBooking.cancelToken}
            />
          ) : null}
          {flowStep === 'picking-slot' && submitError.length > 0 ? (
            <ComingSoon>{submitError}</ComingSoon>
          ) : null}
        </RightColumn>
      </TwoColumnLayout>
    </PageShell>
  )
}

export default BookingPage
